import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { PracticeCallResponse } from './types';

export interface StartPracticeCallParams {
	companyId: string;
	leadServedId: string;
	contactId: string;
}

/**
 * Hook for starting an ElevenLabs practice ("Spar") call session.
 *
 * ElevenLabs is the only voice provider this product runs (mirroring the legacy
 * `VoiceAgentModal`, which pins `provider: 'elevenlabs'`); the OpenAI practice
 * endpoint is intentionally not wired here.
 */
export function useStartElevenLabsPracticeCall() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({
			companyId,
			leadServedId,
			contactId,
		}: StartPracticeCallParams): Promise<PracticeCallResponse> => {
			if (!companyId) {
				throw new Error('Company ID is required');
			}

			const response = await userSession.webServiceHelper.callWebServiceAsync<PracticeCallResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${encodeURIComponent(companyId)}/startPracticeCall/elevenLabs?leadServedId=${encodeURIComponent(leadServedId)}&contactId=${encodeURIComponent(contactId)}`,
				}),
				'POST',
				''
			);

			if (!response.value) {
				throw new Error('No data returned from ElevenLabs practice call API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to start ElevenLabs practice call:', error);
		},
	});
}
